import { Tooltip } from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";
import { connect } from "react-redux";
import { getUser } from "src/login/selector";
import { isRequesting } from "src/requestProgress/helpers";
import { actions, RequestProgressState } from "src/requestProgress/reducer";
import { getRequestProgress } from "src/requestProgress/selector";
import { State } from "src/state";
import smoothScroll from "../../scroll/smoothScroll";
import {
  highlightRows,
  unhighlightRows
} from "../model/highlightedRows/actions";
import {
  mapUnmappedAccount,
  MapUnmappedAccountActionType,
  remapAccount,
  RemapAccountActionType
} from "../model/spread/actions";
import { getSpread } from "../model/spread/selector";
import {
  RuleChangeLogItem,
  Spread,
  SpreadAccountRow,
  SpreadGroundPage
} from "../model/spread/types";

import { DEFAULT_LANGUAGE } from "src/constants";
import { getTagMode } from "../chartOfAccounts/Tag/selector";

export const TOTAL_IMAGE_WIDTH = 2550.0;
export const TOTAL_IMAGE_HEIGHT = 3300.0;

interface SpreadRowHighlightProps {
  highlightRows: typeof highlightRows;
  unhighlightRows: typeof unhighlightRows;
  isAccountSelected?: boolean;
  accountRow: SpreadAccountRow;
  mappingNodeId: string | null;
  isFirstCell?: boolean;
  getScrollContext: () => Element | null;
  spread?: Spread;
  userId: string;
  environmentName?: string;
  remapAccountAction: RemapAccountActionType;
  mapUnmappedAccountAction: MapUnmappedAccountActionType;
  requestProgress: RequestProgressState;
  adjustedOcrText?: string;
  tagMode: boolean;
  groundPage: SpreadGroundPage;
}

export class SpreadRowHighlight extends React.Component<
  SpreadRowHighlightProps
> {
  private domNode: HTMLDivElement | null;
  constructor(props: SpreadRowHighlightProps) {
    super(props);
    this.domNode = null;
  }
  public componentDidMount() {
    this.scrollIfFirst();
  }
  public componentDidUpdate() {
    this.scrollIfFirst();
  }

  public scrollIfFirst() {
    if (this.props.isAccountSelected) {
      // I cannot figure out how to make this work without the timeout
      // because the parent ref is not available when the component first mounts
      // however it's not a react interaction, so it seems ok?
      setTimeout(() => {
        if (!this.domNode) {
          return;
        }
        smoothScroll(this.domNode, this.props.getScrollContext() || window);
      });
    }
  }
  public render() {
    const { accountRow } = this.props;
    const coordX1 = accountRow.coordX1;
    const coordY1 = accountRow.coordY1;
    const coordX2 = accountRow.coordX2;
    const coordY2 = accountRow.coordY2;
    const height =
      this.props.groundPage.height && this.props.groundPage.height !== 0
        ? this.props.groundPage.height
        : TOTAL_IMAGE_HEIGHT;
    const width =
      this.props.groundPage.width && this.props.groundPage.width !== 0
        ? this.props.groundPage.width
        : TOTAL_IMAGE_WIDTH;

    const style: React.CSSProperties = {
      position: "absolute",
      left: `calc(${(coordX1 / width) * 100}% - 8px)`,
      top: `calc(${(coordY1 / height) * 100}% - 2px)`,
      width: `calc(${((coordX2 - coordX1) / TOTAL_IMAGE_WIDTH) * 100}% + 16px)`,
      height: "0px",
      // padding is used here to create the height
      // (https://css-tricks.com/scaled-proportional-blocks-with-css-and-javascript/)
      paddingBottom: `${((coordY2 - coordY1) / TOTAL_IMAGE_WIDTH) * 100}%`
    };

    const tooltipContent = accountRow.parentNodeId ? (
      <div>
        {this.props.spread &&
        this.props.spread.language &&
        this.props.spread.language.toLocaleLowerCase() !== DEFAULT_LANGUAGE ? (
          <div>
            <div className="SpreadPage__highlight__tooltip__header">MAPPED</div>
            <div
              className="SpreadPage__highlight__tooltip__content"
              style={{ textAlign: "center" }}
            >
              <span>
                {this.props.accountRow.asReportedLabel}
                <div> mapped to </div>
                {this.props.accountRow.parentNodeName}
              </span>
            </div>
          </div>
        ) : (
          <div>
            <div className="SpreadPage__highlight__tooltip__header">
              MAPPED TO
            </div>
            <div className="SpreadPage__highlight__tooltip__content">
              {this.props.accountRow.parentNodeName}
            </div>
          </div>
        )}
        <span className="arrow" />
      </div>
    ) : (
      <div>
        <div className="SpreadPage__highlight__tooltip__header">UNMAPPED</div>
        <div className="SpreadPage__highlight__tooltip__content">
          {this.props.adjustedOcrText}
        </div>
        <span className="arrow" />
      </div>
    );

    return (
      <Tooltip
        title={this.props.tagMode ? "" : tooltipContent}
        placement="top-start"
        classes={{
          tooltip: classnames(
            "SpreadPage__highlight__tooltip",
            accountRow.parentNodeId
              ? "SpreadPage__highlight__tooltip--mapped"
              : "SpreadPage__highlight__tooltip--unmapped",
            this.props.mappingNodeId === this.props.accountRow.parentNodeId &&
              "SpreadPage__highlight__tooltip--mapping-mode"
          ),
          popper: "SpreadPage__highlight__popper"
        }}
        onOpen={this.handleOpenTooltip}
        onClose={this.handleCloseTooltip}
      >
        <div
          ref={domNode => (this.domNode = domNode)}
          className={classnames(
            accountRow.parentNodeId
              ? "SpreadPage__highlight SpreadPage__highlight--mapped"
              : "SpreadPage__highlight SpreadPage__highlight--unmapped",
            this.props.isAccountSelected && "SpreadPage__highlight--selected",
            this.props.mappingNodeId === this.props.accountRow.parentNodeId &&
              "SpreadPage__highlight--mapping-mode"
          )}
          style={style}
          onClick={
            accountRow.parentNodeId
              ? this.handleMappedHighlightClick
              : this.handleUnmappedHighlightClick
          }
        />
      </Tooltip>
    );
  }

  private handleOpenTooltip = (event: object) => {
    if (this.props.accountRow.parentNodeId) {
      this.props.highlightRows([
        this.props.accountRow.accountId,
        this.props.accountRow.parentNodeId
      ]);
    }
  };

  private handleCloseTooltip = (event: object) => {
    this.props.unhighlightRows();
  };

  private handleMappedHighlightClick = () => {
    const { spread, mappingNodeId, accountRow, userId } = this.props;

    const isLoading =
      isRequesting(actions.undoRule) ||
      isRequesting(actions.remapAccount) ||
      isRequesting(actions.mapUnmappedAccount) ||
      isRequesting(actions.unmapHiddenAccountRow);

    if (isLoading) {
      return;
    }

    if (mappingNodeId === accountRow.parentNodeId) {
      return;
    }

    if (mappingNodeId && spread) {
      const rule: RuleChangeLogItem = {
        entityId: spread.entityId,
        nodeId: accountRow.topLevelHiddenAccountId,
        fromParentNodeId: accountRow.parentNodeId,
        toParentNodeId: mappingNodeId,
        accountId: accountRow.topLevelHiddenAccountId,
        userId
      };
      this.props.remapAccountAction({
        spreadId: spread.id,
        rule
      });
    }
  };

  private handleUnmappedHighlightClick = () => {
    const isLoading =
      isRequesting(actions.undoRule) ||
      isRequesting(actions.remapAccount) ||
      isRequesting(actions.mapUnmappedAccount) ||
      isRequesting(actions.unmapHiddenAccountRow);

    if (isLoading) {
      return;
    }

    const { spread, mappingNodeId, accountRow } = this.props;
    if (mappingNodeId && spread) {
      this.props.mapUnmappedAccountAction({
        spreadId: spread.id,
        unmappedAccountId: accountRow.id,
        nodeId: mappingNodeId
      });
    }
  };
}

const mapStateToProps = (state: State) => ({
  spread: getSpread(state),
  userId: getUser(state).userId,
  requestProgress: getRequestProgress(state),
  tagMode: getTagMode(state)
});

export default connect(
  mapStateToProps,
  {
    highlightRows,
    unhighlightRows,
    remapAccountAction: remapAccount.action,
    mapUnmappedAccountAction: mapUnmappedAccount.action
  }
)(SpreadRowHighlight);



// WEBPACK FOOTER //
// ./src/spread/rawSpread/SpreadRowHighlight.tsx